import { createAsyncThunk } from '@reduxjs/toolkit';

// Services
import { CounsellorsService } from '../../services/counsellors.service';

// Slices
import { CurrentCounsellorSliceActions } from './current-counsellor.slice';

// Store entities
import { openNotification } from '../app-notifications/app-notifications.thunks';

// Models
import {
  ICostPerSessionCreate, ICostPerSessionDelete, ICostPerSessionUpdate, ICounsellor,
  ICounsellorCallRates,
  ICounsellorUpdateProfile, IFreeMinutes
} from '../../interfaces/counsellors.interfaces';
import { UserSliceActions } from '../user/user.slice';
import { IUserData } from '../../interfaces/user.interfaces';
import { deactivateCounsellor } from '../counsellors/counsellors.thunks';

export enum ECurrentCounsellorThunks {
  GetProfile = 'CURRENT_COUNSELLOR/getProfile',
  GetCostPerSessionRates = 'CURRENT_COUNSELLOR/getCostPerSessionRates',
  GetFreeMinutes = 'CURRENT_COUNSELLOR/getFreeMinutes',
  UpdateCurrentCounsellorInfo = 'CURRENT_COUNSELLOR/updateCurrentCounsellorInfo',
  UpdateStatusCounsellorByRates = 'CURRENT_COUNSELLOR/updateStatusCounsellorByRates',
  UpdateProfile = 'CURRENT_COUNSELLOR/updateProfile',
  UpdateProfileAbout = 'CURRENT_COUNSELLOR/updateProfileAbout',
  UpdateCounsellorPersonalLink = 'CURRENT_COUNSELLOR/updateCounsellorPersonalLink',
  CheckAvailabilityPersonalLink = 'CURRENT_COUNSELLOR/checkAvailabilityPersonalLink',
  UpdatePricePerMessage = 'CURRENT_COUNSELLOR/updatePricePerMessage',
  UpdatePricePerMinute = 'CURRENT_COUNSELLOR/updatePricePerMinute',
  UpdateProfileSpecialtySpecialisations = 'CURRENT_COUNSELLOR/updateProfileSpecialtySpecialisations',
  DeleteProfile = 'CURRENT_COUNSELLOR/deleteProfile',
  CounsellorPhotoUpdate = 'CURRENT_COUNSELLOR/counsellorPhotoUpdate',
  CounsellorPhotoDelete = 'CURRENT_COUNSELLOR/counsellorPhotoDelete',
  CreateCounsellorSessionRate = 'CURRENT_COUNSELLOR/createCounsellorSessionRate',
  UpdateCounsellorSessionRate = 'CURRENT_COUNSELLOR/updateCounsellorSessionRate',
  DeleteCounsellorSessionRate = 'CURRENT_COUNSELLOR/deleteCounsellorSessionRate',
}

export const getCounsellorProfile = createAsyncThunk(
  ECurrentCounsellorThunks.GetProfile,
  async (id: number | string, { dispatch }: any) => {
    dispatch(CurrentCounsellorSliceActions.profileRequest());
    const { worker_profile, error } = await CounsellorsService.fetchCounsellorProfile(id);
    if (!worker_profile) {
      dispatch(CurrentCounsellorSliceActions.profileFailure(error ?? 'Failed to load counsellor data, please reload page and try again!'));
      return null;
    }
    dispatch(getFreeMinutes(worker_profile.id));
    dispatch(CurrentCounsellorSliceActions.profileSuccess(worker_profile));
    return worker_profile;
  }
);

export const getCostPerSessionRates = createAsyncThunk(
  ECurrentCounsellorThunks.GetCostPerSessionRates,
  async (id: number | string, { dispatch }: any) => {
    dispatch(CurrentCounsellorSliceActions.profileRequest());
    const { worker_session_rates, error } = await CounsellorsService.getCostPerSessionRates(id);
    if (!worker_session_rates) {
      dispatch(CurrentCounsellorSliceActions.profileFailure(error ?? 'Failed to load session rates, please reload page and try again!'));
      return;
    }
    dispatch(CurrentCounsellorSliceActions.loadSessionRates({ worker_session_rates }));
    return worker_session_rates;
  }
);

export const getFreeMinutes = createAsyncThunk(
  ECurrentCounsellorThunks.GetFreeMinutes,
  async (id: number | string, { dispatch }: any) => {
    const { free_minutes, error } = await CounsellorsService.getFreeMinutesInfo(id);
    if (!free_minutes) {
      return null;
    }
    dispatch(CurrentCounsellorSliceActions.loadFreeMinutes(free_minutes));
    return free_minutes;
  }
);

export const updateFreeMinutes = createAsyncThunk(
  ECurrentCounsellorThunks.GetFreeMinutes,
  async ({ id, free_minutes }: { id: number | string; free_minutes: IFreeMinutes }, { dispatch }: any) => {
    dispatch(CurrentCounsellorSliceActions.profileRequest());
    const { error } = await CounsellorsService.updateFreeMinutesInfo(id, free_minutes);
    if (error) {
      dispatch(CurrentCounsellorSliceActions.profileFailure(error ?? 'Failed to load free minutes information, please reload page and try again!'));
      return null;
    }
    dispatch(CurrentCounsellorSliceActions.updateFreeMinutes(free_minutes));
    return free_minutes;
  }
);

export const updateCurrentCounsellorInfo = createAsyncThunk(
  ECurrentCounsellorThunks.UpdateCurrentCounsellorInfo,
  async ({ counsellor }: { counsellor: ICounsellor }, { dispatch }) => {
    dispatch(CurrentCounsellorSliceActions.profileUpdateSuccess(counsellor));
    return counsellor;
  }
);

export const updateStatusCounsellorByRates = createAsyncThunk(
  ECurrentCounsellorThunks.UpdateStatusCounsellorByRates,
  async (data: ICounsellor, { dispatch }) => {
    dispatch(CurrentCounsellorSliceActions.updateStatusCounsellorByRates(data));
    return data;
  }
);

export const updateCounsellorProfile = createAsyncThunk(
  ECurrentCounsellorThunks.UpdateProfile,
  async ({ counsellor, worker_id, isCurrentAdmin = false }: {
    counsellor: ICounsellorUpdateProfile,
    worker_id: string | number,
    isCurrentAdmin?: boolean,
  }, { dispatch }) => {
    dispatch(CurrentCounsellorSliceActions.profileUpdateRequest());
    const { error, worker_profile, message } = await CounsellorsService.updateProfile(counsellor, worker_id);
    if (!worker_profile) {
      dispatch(CurrentCounsellorSliceActions.profileUpdateFailure('Error'));
      dispatch(openNotification({
        description: error ?? 'Couldn\'t update the consultant data, try again',
        type: 'error'
      }));
      return null;
    }
    dispatch(openNotification({
      description: message ?? 'Changes successfully saved!',
      type: 'success'
    }));
    if (isCurrentAdmin) {
      dispatch(UserSliceActions.profileFromCounsellorUpdate({
        first_name: worker_profile.first_name,
        last_name: worker_profile.last_name,
        email: worker_profile.email,
        languages: worker_profile.languages,
        country: worker_profile.country
      }));
    }
    dispatch(CurrentCounsellorSliceActions.profileUpdateSuccess(worker_profile));
    return worker_profile;
  }
);

export const updateCounsellorAbout = createAsyncThunk(
  ECurrentCounsellorThunks.UpdateProfileAbout,
  async ({ about, worker_id }: { about: string, worker_id: string | number }, { dispatch }) => {
    dispatch(CurrentCounsellorSliceActions.profileUpdateRequest());
    const { error, worker_profile, message } = await CounsellorsService.updateProfileAbout(about, worker_id);
    if (!worker_profile) {
      dispatch(CurrentCounsellorSliceActions.profileUpdateFailure('Error'));
      dispatch(openNotification({
        description: error ?? 'Couldn\'t update the consultant about, try again',
        type: 'error'
      }));
      return null;
    }
    dispatch(openNotification({
      description: message ?? 'Changes successfully saved!',
      type: 'success'
    }));
    dispatch(CurrentCounsellorSliceActions.profileUpdateSuccess(worker_profile));
    return worker_profile;
  }
);

export const updateCounsellorSpecialtySpecialisations = createAsyncThunk(
  ECurrentCounsellorThunks.UpdateProfileSpecialtySpecialisations,
  async ({ specialities, specializations, worker_id }: {
    specialities: number[],
    specializations: number[] | string[],
    worker_id: string | number
  }, { dispatch }) => {
    dispatch(CurrentCounsellorSliceActions.profileUpdateRequest());
    const {
      error,
      worker_profile,
      message
    } = await CounsellorsService.updateProfileSpecialtySpecialisations(specialities, specializations, worker_id);
    if (!worker_profile) {
      dispatch(CurrentCounsellorSliceActions.profileUpdateFailure(error ?? 'Couldn\'t update the consultant about, try again'));
      dispatch(openNotification({
        description: error ?? 'Couldn\'t update the consultant about, try again',
        type: 'error'
      }));
      return null;
    }
    dispatch(openNotification({
      description: message ?? 'Changes successfully saved!',
      type: 'success'
    }));
    dispatch(CurrentCounsellorSliceActions.profileUpdateSuccess(worker_profile));
    return worker_profile;
  }
);

export const updateCounsellorPersonalLink = createAsyncThunk(
  ECurrentCounsellorThunks.UpdateCounsellorPersonalLink,
  async ({ personal_link, worker_id }: { personal_link: string, worker_id: string | number }, { dispatch }) => {
    dispatch(CurrentCounsellorSliceActions.profileUpdateRequest());
    const { error, worker_profile } = await CounsellorsService.updateProfilePersonalLink(personal_link, worker_id);
    if (!worker_profile) {
      dispatch(CurrentCounsellorSliceActions.profileUpdateFailure(error ?? 'Couldn\'t update the consultant personal link, try again'));
      dispatch(openNotification({
        description: error ?? 'Couldn\'t update the consultant personal link, try again',
        type: 'error'
      }));
      return null;
    }
    dispatch(openNotification({
      description: 'Changes successfully saved!',
      type: 'success'
    }));
    dispatch(CurrentCounsellorSliceActions.profileUpdateSuccess(worker_profile));
    return worker_profile;
  }
);

export const checkAvailabilityPersonalLink = createAsyncThunk(
  ECurrentCounsellorThunks.CheckAvailabilityPersonalLink,
  async (personal_link: string, { dispatch }) => {
    const { error } = await CounsellorsService.checkAvailabilityPersonalLink(personal_link);
    if (error) {
      return false;
    }
    return true;
  }
);

export const updatePricePerMessage = createAsyncThunk(
  ECurrentCounsellorThunks.UpdatePricePerMessage,
  async ({ message_rate, is_message_rate_enabled, worker_id }: {
    message_rate?: number | string,
    is_message_rate_enabled: boolean,
    worker_id: string | number
  }, { dispatch }) => {
    dispatch(CurrentCounsellorSliceActions.profileUpdateRequest());
    const {
      error,
      worker_default_rates,
      rates_enabled_count
    } = await CounsellorsService.updatePricePerMessage(is_message_rate_enabled, worker_id, message_rate);
    if (!worker_default_rates) {
      dispatch(CurrentCounsellorSliceActions.profileUpdateFailure(error ?? 'Couldn\'t update the consultant price per messages, try again'));
      dispatch(openNotification({
        description: error ?? 'Couldn\'t update the consultant price per messages, try again',
        type: 'error'
      }));
      return null;
    }
    dispatch(openNotification({
      description: 'Changes successfully saved!',
      type: 'success'
    }));
    dispatch(CurrentCounsellorSliceActions.updatePricePerMessage({ worker_default_rates, rates_enabled_count }));
    return worker_default_rates;
  }
);

export const updatePricePerMinute = createAsyncThunk(
  ECurrentCounsellorThunks.UpdatePricePerMinute,
  async (rates: ICounsellorCallRates, { dispatch }) => {
    dispatch(CurrentCounsellorSliceActions.profileUpdateRequest());
    const { error, worker_default_rates, rates_enabled_count } = await CounsellorsService.updatePricePerMinute(rates);
    if (!worker_default_rates) {
      dispatch(CurrentCounsellorSliceActions.profileUpdateFailure(error ?? 'Couldn\'t update the consultant price per minute, try again'));
      dispatch(openNotification({
        description: error ?? 'Couldn\'t update the consultant price per minute, try again',
        type: 'error'
      }));
      return null;
    }
    dispatch(openNotification({
      description: 'Changes successfully saved!',
      type: 'success'
    }));
    dispatch(CurrentCounsellorSliceActions.updatePricePerMessage({ worker_default_rates, rates_enabled_count }));
    return worker_default_rates;
  }
);

export const createCounsellorSessionRate = createAsyncThunk(
  ECurrentCounsellorThunks.CreateCounsellorSessionRate,
  async (data: ICostPerSessionCreate, { dispatch }) => {
    dispatch(CurrentCounsellorSliceActions.profileUpdateRequest());
    const {
      error,
      worker_session_rates,
      rates_enabled_count
    } = await CounsellorsService.createCounsellorSessionRate(data);
    if (!worker_session_rates) {
      dispatch(CurrentCounsellorSliceActions.profileUpdateFailure(error ?? 'Couldn\'t create the consultant session rate, try again'));
      dispatch(openNotification({
        description: error ?? 'Couldn\'t create the consultant session rate, try again',
        type: 'error'
      }));
      return null;
    }
    dispatch(openNotification({
      description: 'Changes successfully saved!',
      type: 'success'
    }));
    dispatch(CurrentCounsellorSliceActions.loadSessionRates({ worker_session_rates, rates_enabled_count }));
    return worker_session_rates;
  }
);

export const updateCounsellorSessionRate = createAsyncThunk(
  ECurrentCounsellorThunks.UpdateCounsellorSessionRate,
  async (data: ICostPerSessionUpdate, { dispatch }) => {
    dispatch(CurrentCounsellorSliceActions.profileUpdateRequest());
    const {
      error,
      worker_session_rates,
      rates_enabled_count
    } = await CounsellorsService.updateCounsellorSessionRate(data);
    if (!worker_session_rates) {
      dispatch(CurrentCounsellorSliceActions.profileUpdateFailure(error ?? 'Couldn\'t update the consultant session rate, try again'));
      dispatch(openNotification({
        description: error ?? 'Couldn\'t update the consultant session rate, try again',
        type: 'error'
      }));
      return null;
    }
    dispatch(openNotification({
      description: 'Changes successfully saved!',
      type: 'success'
    }));
    dispatch(CurrentCounsellorSliceActions.loadSessionRates({ worker_session_rates, rates_enabled_count }));
    return worker_session_rates;
  }
);

export const deleteCounsellorSessionRate = createAsyncThunk(
  ECurrentCounsellorThunks.DeleteCounsellorSessionRate,
  async (data: ICostPerSessionDelete, { dispatch }) => {
    dispatch(CurrentCounsellorSliceActions.profileUpdateRequest());
    const {
      error,
      worker_session_rates,
      rates_enabled_count
    } = await CounsellorsService.deleteCounsellorSessionRate(data);
    if (!worker_session_rates) {
      dispatch(CurrentCounsellorSliceActions.profileUpdateFailure(error ?? 'Couldn\'t delete the consultant session rate, try again'));
      dispatch(openNotification({
        description: error ?? 'Couldn\'t delete the consultant session rate, try again',
        type: 'error'
      }));
      return null;
    }
    dispatch(openNotification({
      description: 'Changes successfully saved!',
      type: 'success'
    }));
    dispatch(CurrentCounsellorSliceActions.loadSessionRates({ worker_session_rates, rates_enabled_count }));
    return worker_session_rates;
  }
);

export const deleteCounsellorProfile = createAsyncThunk(
  ECurrentCounsellorThunks.DeleteProfile,
  async (_, { dispatch }) => {
    dispatch(CurrentCounsellorSliceActions.profileDeleteRequest());
    const res = await CounsellorsService.deleteProfile();
    if (res.response && res.response.status !== 200) {
      dispatch(CurrentCounsellorSliceActions.profileDeleteFailure('Error'));
      return res.response.data;
    }
    dispatch(CurrentCounsellorSliceActions.profileDeleteSuccess());
    return res.data;
  }
);

export const counsellorPhotoUpdate = createAsyncThunk(
  ECurrentCounsellorThunks.CounsellorPhotoUpdate,
  async ({ file, worker_id, isCurrentAdmin = false }: {
    file: File,
    worker_id: string | number,
    isCurrentAdmin?: boolean
  }, { dispatch }) => {
    dispatch(CurrentCounsellorSliceActions.updatePhotoRequest());
    const { message, photo, error } = await CounsellorsService.updatePhoto(file, worker_id);
    if (!photo) {
      dispatch(CurrentCounsellorSliceActions.updatePhotoFailure(error ?? 'Couldn\'t update the consultant photo, try again'));
      dispatch(openNotification({
        description: error ?? 'Couldn\'t update the consultant photo, try again',
        type: 'error'
      }));
      return null;
    }
    dispatch(openNotification({
      description: message ?? 'Changes successfully saved!',
      type: 'success'
    }));
    if (isCurrentAdmin) {
      dispatch(UserSliceActions.updatePhotoSuccess({ photo }));
    }
    dispatch(CurrentCounsellorSliceActions.updatePhotoSuccess({ photo }));
    return photo;
  }
);

export const counsellorPhotoDelete = createAsyncThunk(
  ECurrentCounsellorThunks.CounsellorPhotoDelete,
  async ({ worker_id }: { worker_id: string | number }, { dispatch }) => {
    dispatch(CurrentCounsellorSliceActions.deletePhotoRequest());
    const { error, message } = await CounsellorsService.deletePhoto(worker_id);
    if (error) {
      dispatch(CurrentCounsellorSliceActions.deletePhotoFailure('Error'));
      dispatch(openNotification({
        description: error ?? 'Couldn\'t delete the consultant photo, try again',
        type: 'success'
      }));
      return false;
    }
    dispatch(openNotification({
      description: message ?? 'Changes successfully saved!',
      type: 'success'
    }));
    dispatch(CurrentCounsellorSliceActions.deletePhotoSuccess());
    return true;
  }
);
